// src/pages/ForgotPasswordPage.tsx

import React, { useState } from 'react';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { userPool } from '../services/cognitoConfig';
import { useNavigate, Link } from 'react-router-dom';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import logo from '../assets/images/readLinker_mini_logo_128.png';
import '../css/LoginPage.css';

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'white',
        borderRadius: 1,
    },
    '& .MuiOutlinedInput-input': {
        color: 'rgba(0, 0, 0, 0.87)',
    },
    '& input:-webkit-autofill': {
        WebkitBoxShadow: '0 0 0 1000px white inset',
        WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
    },
    '& .MuiInputLabel-root': {
        backgroundColor: 'white',
        padding: '0 4px',
        color: 'rgba(0, 0, 0, 0.6)',
        '&.Mui-focused': {
            color: 'rgba(0, 0, 0, 0.6)',
        },
    },
};

const ForgotPasswordPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [codeSent, setCodeSent] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const navigate = useNavigate();

    const passwordTooShort = newPassword.length > 0 && newPassword.length < 8;
    const passwordValid = newPassword.length >= 8;

    const handleRequestCode = (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;
        setError(null);
        setSubmitting(true);
        const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
        cognitoUser.forgotPassword({
            onSuccess: () => {
                setSubmitting(false);
                setCodeSent(true);
                setMessage('パスワードリセットのコードをメールに送信しました。');
            },
            onFailure: (err) => {
                console.error('forgotPassword failed', err);
                setSubmitting(false);
                setError('コードの送信に失敗しました。メールアドレスをご確認ください。');
            },
        });
    };

    const handleConfirm = (e: React.FormEvent) => {
        e.preventDefault();
        if (!passwordValid || submitting) return;
        setError(null);
        setSubmitting(true);
        const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
        cognitoUser.confirmPassword(code, newPassword, {
            onSuccess: () => {
                setSubmitting(false);
                navigate('/login', {
                    state: { message: 'パスワードの再設定が完了しました。新しいパスワードでログインしてください。' },
                });
            },
            onFailure: (err) => {
                console.error('confirmPassword failed', err);
                setSubmitting(false);
                setError('コードの確認に失敗しました。コードとパスワードをご確認の上、再度お試しください。');
            },
        });
    };

    return (
        <div className="login-page">
            <Container maxWidth="xs">
                <Box
                    sx={{
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        color: 'white',
                        py: 20,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            width: '100%',
                        }}
                    >
                        <img src={logo} alt="ReadLinker logo" style={{ width: '128px', height: '128px' }} />
                        <Typography component="h1" variant="h5" sx={{ marginTop: 2 }}>
                            パスワードの再設定
                        </Typography>

                        {!codeSent && (
                            <Box component="form" onSubmit={handleRequestCode} sx={{ mt: 3, width: '100%' }}>
                                <TextField
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="email"
                                    label="メールアドレス"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    autoFocus
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    sx={fieldSx}
                                />
                                {error && <Typography variant="body2" sx={{ color: 'red', mt: 1 }}>{error}</Typography>}
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    disabled={!email || submitting}
                                    sx={{ mt: 3, mb: 2 }}
                                >
                                    {submitting ? '送信中…' : 'リセットコードを送信'}
                                </Button>
                            </Box>
                        )}

                        {codeSent && (
                            <Box component="form" onSubmit={handleConfirm} sx={{ mt: 3, width: '100%' }}>
                                {message && (
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 1 }}>
                                        {message}
                                    </Typography>
                                )}
                                <TextField
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="code"
                                    label="確認コード"
                                    name="code"
                                    autoFocus
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="newPassword"
                                    label="新しいパスワード"
                                    name="newPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    sx={fieldSx}
                                />
                                {passwordTooShort && (
                                    <Typography variant="body2" sx={{ color: 'red', mt: 0.5, fontSize: 12 }}>
                                        パスワードは8文字以上で入力してください。
                                    </Typography>
                                )}
                                <Typography
                                    variant="body2"
                                    onClick={() => setShowPassword((v) => !v)}
                                    sx={{ color: 'rgba(255,255,255,0.7)', mt: 1, cursor: 'pointer', textAlign: 'right' }}
                                >
                                    {showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                                </Typography>
                                {error && <Typography variant="body2" sx={{ color: 'red', mt: 1 }}>{error}</Typography>}
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    disabled={!passwordValid || !code || submitting}
                                    sx={{ mt: 3, mb: 2 }}
                                >
                                    {submitting ? '更新中…' : 'パスワードを再設定'}
                                </Button>
                            </Box>
                        )}

                        <Typography variant="body2" sx={{ marginTop: 2 }}>
                            <Link to="/login" style={{ color: 'white' }}>ログイン画面に戻る</Link>
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ textAlign: 'center' }}>
                        © 2023-2026 ADA LLC.
                    </Typography>
                </Box>
            </Container>
        </div>
    );
};

export default ForgotPasswordPage;
